import React from "react";
import { Paper, Stack, Typography, Box } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import type { LeadCustomFields as LeadCustomFieldsMap, LeadCustomFieldValue } from "./leadApi";

/* Premium surface tokens — kept in sync with LeadDetailPage.SURFACE so
   the Inquiry Details card is visually part of the same page system. */
const SURFACE = {
  card:       "#ffffff",
  cardBorder: "1px solid #eef0f4",
  cardShadow: "0 1px 2px rgba(13,27,42,0.04), 0 4px 16px rgba(13,27,42,0.04)",
  cardRadius: "14px",
} as const;

const NAVY = "#16233A";
const GOLD = "#7C4DFF";

function formatValue(v: LeadCustomFieldValue): string {
  if (Array.isArray(v)) {
    return v.map((x) => formatValue(x as LeadCustomFieldValue)).filter((s) => s !== "").join(", ");
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined) return "";
  return String(v);
}

function isEmpty(v: LeadCustomFieldValue): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0 || v.every(isEmpty as (x: unknown) => boolean);
  return false;
}

interface Props {
  fields?: LeadCustomFieldsMap | null;
  /** Section heading — defaults to "Inquiry Details". */
  title?: string;
  /** Optional hint shown when no renderable fields are present. Omit to render nothing. */
  emptyHint?: string;
}

/**
 * Renders backend-normalized website form fields as a clean two-column
 * (single-column on mobile) label/value grid. Fully generic — no form,
 * source or company knowledge lives here; the backend normalizer supplies
 * the human-readable label for every key.
 */
const LeadCustomFields: React.FC<Props> = ({ fields, title = "Inquiry Details", emptyHint }) => {
  const entries = React.useMemo(() => {
    if (!fields || typeof fields !== "object") return [];
    return Object.entries(fields)
      .filter(([, f]) => f && typeof f === "object" && !isEmpty(f.value))
      .map(([key, f]) => ({
        key,
        label: (f.label && String(f.label).trim()) || key,
        display: formatValue(f.value),
      }))
      .filter((row) => row.display !== "");
  }, [fields]);

  if (entries.length === 0) {
    if (!emptyHint) return null;
    return (
      <Paper elevation={0} sx={{
        mb: 1.6, px: 2.2, py: 1.6,
        borderRadius: SURFACE.cardRadius,
        border: SURFACE.cardBorder,
        bgcolor: SURFACE.card,
        boxShadow: SURFACE.cardShadow,
      }}>
        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: GOLD, textTransform: "uppercase", mb: 0.6 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">{emptyHint}</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{
      mb: 1.6, px: 2.2, py: 1.6,
      borderRadius: SURFACE.cardRadius,
      border: SURFACE.cardBorder,
      bgcolor: SURFACE.card,
      boxShadow: SURFACE.cardShadow,
    }}>
      <Stack direction="row" alignItems="center" gap={1} mb={1.2}>
        <Box sx={{
          width: 28, height: 28, borderRadius: 1.2,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: `${GOLD}18`, color: GOLD,
        }}>
          <ListAltIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1, color: NAVY, textTransform: "uppercase" }}>
          {title}
        </Typography>
      </Stack>

      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        columnGap: 3,
        rowGap: 1.2,
      }}>
        {entries.map((row) => (
          <Stack key={row.key} direction="row" alignItems="baseline" gap={1.5} sx={{ minWidth: 0 }}>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 700, color: "#94a3b8",
              letterSpacing: 0.5, textTransform: "uppercase",
              minWidth: 140, flexShrink: 0,
            }}>
              {row.label}
            </Typography>
            <Typography sx={{
              fontSize: 13.5, fontWeight: 600, color: "#16233A",
              lineHeight: 1.35, wordBreak: "break-word",
            }}>
              {row.display}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Paper>
  );
};

export default LeadCustomFields;
